import { faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";
import { faArrowRightToBracket, faCircleNotch, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import isEmail from "validator/lib/isEmail";
import authApi from "../../../api/authApi";
import AuthHeader from "../../../components/AuthHeader";
import { useDispatch } from "react-redux";
import { authActions } from "../../../store/authSlice";
import handleErrorAsync from "../../../utils/handleErrorAsync";
import { firstAllowedPath } from "../../../config/admin-permissions";

function Login() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [eye, setEye] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [searchParams] = useSearchParams();

    const redirectParam = searchParams.get("redirect");
    const [input, setInput] = useState({
        email: {
            name: "email",
            type: "text",
            placeholder: "Email",
            validate: true,
            value: "",
            msg: "Địa chỉ email có dạng: vidu@example.com",
        },
        password: {
            name: "password",
            type: "password",
            placeholder: "Mật khẩu",
            validate: true,
            value: "",
            msg: "Mật khẩu không thể bỏ trống",
        },
    });

    const handleInput = (e) => {
        setErrorMsg("");
        setInput((prev) => ({
            ...prev,
            [e.target.name]: {
                ...prev[e.target.name],
                value: e.target.value.trim(),
                validate: true,
            },
        }));
    };

    const validator = (value, type) => {
        switch (type) {
            case "email":
                return isEmail(value);
            case "password":
                return value.trim().length > 0;
            default:
                break;
        }
    };

    const handleBlur = (e) => {
        const name = e.target.name;
        const value = e.target.value;
        setInput((prev) => ({
            ...prev,
            [name]: { ...prev[name], validate: validator(value, name) },
        }));
    };

    const handleOpenEye = () => {
        setEye((prev) => !prev);
    };

    const handleSubmit = async () => {
        if (!validateAll) return;

        setIsLoading(true);
        try {
            const res = await authApi.login({
                email: input.email.value,
                password: input.password.value,
            });
            const role = res.data.user.role;
            // Cho phép admin và mod (CTV quyền giới hạn) vào admin panel.
            if (role !== "admin" && role !== "mod") {
                setIsLoading(false);
                return setErrorMsg("Bạn không có quyền truy cập!");
            }

            const { name, avatar } = res.data.user;
            localStorage.setItem("access_token", res.data.token);
            localStorage.setItem("user_info", JSON.stringify({ name, avatar }));
            dispatch(authActions.login());
            dispatch(authActions.setUserInfo(res.data.user));
            // mod: vào mục đầu tiên được cấp quyền; admin: dashboard.
            if (redirectParam) navigate(`/${redirectParam}`);
            else navigate(role === "mod" ? firstAllowedPath(res.data.user) : "/");
        } catch (error) {
            console.log(error);
            handleErrorAsync(error, setErrorMsg);
        }
        setIsLoading(false);
    };

    const handleEnter = (e) => {
        if (e.key === "Enter") handleSubmit();
    };

    const validateAll = Object.keys(input).every((el) => validator(input[el].value, el));

    useEffect(() => {
        setInput((prev) => ({
            ...prev,
            password: {
                ...prev.password,
                type: eye ? "text" : "password",
            },
        }));
    }, [eye]);

    return (
        <div onKeyDown={handleEnter} className="h-[100dvh]  flex flex-col p-6 items-center justify-between md:w-[768px]  md:mx-auto">
            <AuthHeader />
            <div className="flex w-full  flex-col gap-8 select-none">
                <div className="flex flex-col gap-4">
                    <span className="flex justify-center">
                        <FontAwesomeIcon className="text-2xl p-4 rounded-lg border" icon={faArrowRightToBracket} />
                    </span>
                    <span className="text-xl font-semibold">Đăng nhập</span>
                </div>
                {errorMsg && (
                    <div className="flex justify-center items-center gap-1 text-sm text-red-500 ">
                        <FontAwesomeIcon icon={faXmarkCircle} /> <span>{errorMsg}</span>
                    </div>
                )}
                <form className="flex flex-col gap-4">
                    {Object.keys(input).map((el, index) => (
                        <div key={index} className="relative">
                            <input
                                autoCapitalize="off"
                                className={`py-3  w-full bg-transparent border-line-strong ${input[el].validate ? "border-b" : "border-b border-b-red-500"} `}
                                type={input[el].type}
                                name={input[el].name}
                                value={input[el].value}
                                placeholder={input[el].placeholder}
                                onChange={handleInput}
                                onBlur={handleBlur}
                            />
                            {el === "password" && input[el].value && (
                                <span onClick={handleOpenEye} className="absolute top-1/2 -translate-y-1/2 right-2">
                                    {eye ? <FontAwesomeIcon icon={faEyeSlash} /> : <FontAwesomeIcon icon={faEye} />}
                                </span>
                            )}
                            {!input[el].validate && <span className="text-sm text-red-500">{input[el].msg}</span>}
                        </div>
                    ))}
                </form>
                <div className="flex flex-col gap-2">
                    <div className="flex justify-end">
                        <Link to="/forgot-password" className="text-sm text-pink-800">
                            Quên mật khẩu
                        </Link>
                    </div>
                    <button onClick={handleSubmit} disabled={!validateAll} className={`p-3 bg-pink-600 text-white rounded-lg ${!validateAll && "opacity-60 cursor-not-allowed"}`}>
                        {isLoading ? <FontAwesomeIcon className="animate-spin" icon={faCircleNotch} /> : "Đăng nhập"}
                    </button>
                </div>
                <div className="flex justify-center gap-1 text-sm">
                    <span>Bạn chưa có tài khoản?</span>{" "}
                    <Link to="/signup" className="text-pink-600 underline">
                        Đăng ký
                    </Link>
                </div>
            </div>
            <div className="w-full h-10 flex items-end"></div>
        </div>
    );
}

export default Login;
